import React from "react"
import { useState, useEffect } from "react"
import { Table, Button, Form, Modal, Spinner, Alert, Badge, Row, Col } from "react-bootstrap"
import { FaEdit, FaToggleOn, FaToggleOff, FaSync, FaCheck, FaTimes, FaSlidersH } from "react-icons/fa"
import Swal from "sweetalert2"
import { motion, AnimatePresence, Variants } from "framer-motion"
import "../../../css/discountLimitsManagement.css"
import discountLimitsService from "../../../services/discountLimitsService"

interface User {
  id: number
  name: string
  firstname: string
  email: string
  discount_limit_percentage: number
  discount_limit_active: boolean
  discount_limit_updated_at: string | null
}

interface DiscountLimitsManagementProps {
  className?: string
}

const DiscountLimitsManagement: React.FC<DiscountLimitsManagementProps> = ({ className }) => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [newPercentage, setNewPercentage] = useState<number>(0)
  const [newActive, setNewActive] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set())

  // Bulk
  const [showBulkModal, setShowBulkModal] = useState<boolean>(false)
  const [bulkPercentage, setBulkPercentage] = useState<number>(0)
  const [bulkActive, setBulkActive] = useState<boolean>(false)
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false)
  const [applyToFilteredOnly, setApplyToFilteredOnly] = useState<boolean>(false)
  const [applyToSelectedOnly, setApplyToSelectedOnly] = useState<boolean>(false)

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 100,
      },
    },
  }

  useEffect(() => {
    fetchUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Token d'authentification manquant")

      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/discount-limits/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) throw new Error("Erreur lors de la récupération des utilisateurs")

      const data = await res.json()
      if (data && Array.isArray(data.users)) setUsers(data.users)
      else throw new Error("Format de réponse invalide")
    } catch (err: any) {
      console.error("Erreur fetchUsers:", err)
      setError(err?.message || "Impossible de charger les utilisateurs")
    } finally {
      setLoading(false)
    }
  }

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // ---------- Selection helpers ----------
  const isAllFilteredSelected = () => {
    if (filteredUsers.length === 0) return false
    return filteredUsers.every((u) => selectedIds.has(u.id))
  }

  const toggleSelect = (userId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      const allSelected = filteredUsers.every((u) => next.has(u.id))
      if (allSelected) {
        // unselect all filtered
        filteredUsers.forEach((u) => next.delete(u.id))
      } else {
        // select all filtered
        filteredUsers.forEach((u) => next.add(u.id))
      }
      return next
    })
  }

  const clearSelection = () => setSelectedIds(new Set())

  // ---------- Individual actions using frontend service ----------
  const handleEditLimit = (user: User) => {
    setSelectedUser(user)
    setNewPercentage(user.discount_limit_percentage)
    setNewActive(user.discount_limit_active)
    setShowModal(true)
  }

  const handleUpdateLimit = async () => {
    if (!selectedUser) return
    setSubmitting(true)
    try {
      const resp = await discountLimitsService.updateUserLimit({
        userId: selectedUser.id,
        percentage: newPercentage,
        active: newActive,
      })

      if (!resp.success) throw new Error(resp.message || "Erreur serveur")

      setShowModal(false)
      await fetchUsers()

      Swal.fire({ title: "Succès", text: "Limite mise à jour", icon: "success", confirmButtonColor: "#2A919D" })
    } catch (err: any) {
      console.error("handleUpdateLimit:", err)
      Swal.fire({ title: "Erreur", text: err?.message || "Erreur lors de la mise à jour", icon: "error", confirmButtonColor: "#2A919D" })
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleLimit = async (userId: number, currentActive: boolean) => {
    if (updatingIds.has(userId)) return

    setUpdatingIds((prev) => new Set(prev).add(userId))
    try {
      const user = users.find((u) => u.id === userId)
      if (!user) throw new Error("Utilisateur introuvable")

      const resp = await discountLimitsService.updateUserLimit({
        userId,
        percentage: user.discount_limit_percentage,
        active: !currentActive,
      })

      if (!resp.success) throw new Error(resp.message || "Erreur serveur")

      await fetchUsers()
    } catch (err: any) {
      console.error("handleToggleLimit:", err)
      Swal.fire({ title: "Erreur", text: err?.message || "Erreur lors du toggle", icon: "error", confirmButtonColor: "#2A919D" })
    } finally {
      setUpdatingIds((prev) => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  // ---------- Bulk actions using service + filters/selection priority ----------
  const openBulkModal = () => {
    setBulkPercentage(0)
    setBulkActive(false)
    // si une recherche est active, proposer d'appliquer sur les résultats filtrés
    setApplyToFilteredOnly(Boolean(searchTerm))
    // proposer selection uniquement si on a des selections
    setApplyToSelectedOnly(false)
    setShowBulkModal(true)
  }

  const computeTargetUserIds = () => {
    // priority: if applyToSelectedOnly and have selections -> use selections
    if (applyToSelectedOnly && selectedIds.size > 0) return Array.from(selectedIds)
    // else if applyToFilteredOnly -> use filtered
    if (applyToFilteredOnly) return filteredUsers.map((u) => u.id)
    // else undefined -> applies to all
    return undefined
  }

  const handleUpdateAllLimits = async () => {
    const targetUserIds = computeTargetUserIds()

    const scopeText = targetUserIds ? `${targetUserIds.length} utilisateurs` : "tous les utilisateurs"

    const confirm = await Swal.fire({
      title: "Modifier toutes les limites",
      html: `Appliquer <b>${bulkPercentage}%</b> et statut <b>${bulkActive ? "actif" : "inactif"}</b> à ${scopeText} ?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Oui, appliquer",
      cancelButtonText: "Annuler",
    })

    if (!confirm.isConfirmed) return

    setBulkSubmitting(true)
    try {
      const resp = await discountLimitsService.updateAllLimits({
        percentage: bulkPercentage,
        active: bulkActive,
        filter: targetUserIds ? { userIds: targetUserIds } : undefined,
      })

      if (!resp.success) throw new Error(resp.message || "Erreur serveur")

      setShowBulkModal(false)
      // clear selection if applied to selection
      if (applyToSelectedOnly) clearSelection()

      await fetchUsers()

      Swal.fire({ title: "Succès", text: "Mise à jour globale effectuée", icon: "success", confirmButtonColor: "#2A919D" })
    } catch (err: any) {
      console.error("handleUpdateAllLimits:", err)
      Swal.fire({ title: "Erreur", text: err?.message || "Erreur lors de la mise à jour globale", icon: "error", confirmButtonColor: "#2A919D" })
    } finally {
      setBulkSubmitting(false)
    }
  }

  const handleToggleAll = async (activate: boolean) => {
    const targetUserIds = computeTargetUserIds()

    const scopeText = targetUserIds ? `${targetUserIds.length} utilisateurs` : "tous les utilisateurs"

    const result = await Swal.fire({
      title: `${activate ? "Activation" : "Désactivation"} globale`,
      html: `Êtes-vous sûr de vouloir <b>${activate ? "activer" : "désactiver"}</b> ${scopeText} ?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Oui, ${activate ? "activer" : "désactiver"}`,
      cancelButtonText: "Annuler",
    })

    if (!result.isConfirmed) return

    try {
      const resp = await discountLimitsService.toggleAllLimits({ active: activate, filter: targetUserIds ? { userIds: targetUserIds } : undefined })
      if (!resp.success) throw new Error(resp.message || "Erreur serveur")

      if (applyToSelectedOnly) clearSelection()
      await fetchUsers()
      Swal.fire({ title: "Succès", text: `Statut mis à jour pour ${resp.data?.affected ?? "N/A"} utilisateurs`, icon: "success", confirmButtonColor: "#2A919D" })
    } catch (err: any) {
      console.error("handleToggleAll:", err)
      Swal.fire({ title: "Erreur", text: err?.message || "Erreur lors de l'opération globale", icon: "error", confirmButtonColor: "#2A919D" })
    }
  }

  const handleResetAll = async () => {
    const targetUserIds = computeTargetUserIds()
    const scopeText = targetUserIds ? `${targetUserIds.length} utilisateurs` : "tous les utilisateurs"

    const result = await Swal.fire({
      title: "Remise à zéro globale",
      html: `Remettre à 0% et désactiver ${scopeText} ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, remettre à zéro",
      cancelButtonText: "Annuler",
    })

    if (!result.isConfirmed) return

    try {
      const resp = await discountLimitsService.resetAllLimits({ filter: targetUserIds ? { userIds: targetUserIds } : undefined })
      if (!resp.success) throw new Error(resp.message || "Erreur serveur")

      if (applyToSelectedOnly) clearSelection()
      await fetchUsers()
      Swal.fire({ title: "Succès", text: `Remise à zéro effectuée (${resp.data?.affected ?? "N/A"})`, icon: "success", confirmButtonColor: "#2A919D" })
    } catch (err: any) {
      console.error("handleResetAll:", err)
      Swal.fire({ title: "Erreur", text: err?.message || "Erreur lors de la remise à zéro", icon: "error", confirmButtonColor: "#2A919D" })
    }
  }

  const getStatusBadge = (active: boolean) => {
    return (
      <Badge className={active ? "dlm-badge-active" : "dlm-badge-inactive"}>
        {active ? "Actif" : "Inactif"}
      </Badge>
    )
  }

  const getPercentageBadge = (percentage: number) => {
    let badgeClass = "dlm-badge-percentage-zero"

    if (percentage > 0 && percentage < 25) {
      badgeClass = "dlm-badge-percentage-low"
    } else if (percentage >= 25 && percentage < 50) {
      badgeClass = "dlm-badge-percentage-medium"
    } else if (percentage >= 50 && percentage < 75) {
      badgeClass = "dlm-badge-percentage-high"
    } else if (percentage >= 75) {
      badgeClass = "dlm-badge-percentage-very-high"
    }

    return (
      <Badge className={badgeClass}>
        {percentage}%
      </Badge>
    )
  }

  return (
    <div className={`dlm-container p-4 ${className || ""}`}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="dlm-header-section mb-4"
      >
        <Row className="align-items-center">
          <Col md={6}>
            <h5 className="dlm-section-title">Limites de remise par utilisateur</h5>
            <p className="text-muted mb-0 small">Configurez les limites de remise pour chaque utilisateur</p>
          </Col>
          <Col md={6}>
            <div className="dlm-button-group d-flex justify-content-end gap-1 align-items-center">
              <Button
                variant=""
                size="sm"
                onClick={handleResetAll}
                className="dlm-btn-reset-all">
                <FaSync /> Remise à zéro
              </Button>

              <Button
                variant=""
                size="sm"
                onClick={openBulkModal}
                className="dlm-btn-bulk-update">
                <FaSlidersH /> Modifier tout
              </Button>

              <Button
                variant=""
                size="sm"
                onClick={() => handleToggleAll(true)}
                className="dlm-btn-toggle-all-active">
                <FaCheck /> Activer tout
              </Button>

              <Button
                variant=""
                size="sm"
                onClick={() => handleToggleAll(false)}
                className="dlm-btn-toggle-all-disable"
              >
                <FaTimes /> Désactiver tout
              </Button>
            </div>
          </Col>
        </Row>
      </motion.div>

      <div className="dlm-search-section">
        <Form.Control
          type="text"
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="dlm-search-input"
          size="sm"
        />
      </div>

      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="dlm-loading-container py-3">
          <Spinner animation="border" role="status" size="sm" className="dlm-spinner">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      ) : (
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="dlm-table-container">
          <div className="dlm-table-responsive">
            <Table striped bordered hover size="sm" className="dlm-discount-limits-table">
              <thead className="dlm-table-light">
                <tr>
                  <th style={{ width: '10px', minWidth: '10px' }}>
                    <Form.Check
                      type="checkbox"
                      aria-label="Sélectionner tout"
                      checked={isAllFilteredSelected()}
                      onChange={toggleSelectAllFiltered}
                    />
                  </th>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>Limite (%)</th>
                  <th>Statut</th>
                  <th>Dernière MAJ</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <motion.tr key={user.id} variants={itemVariants} exit={{ opacity: 0, x: -100 }} layout>
                        <td style={{ width: '15px', minWidth: '15px' }}>
                          <Form.Check
                            type="checkbox"
                            aria-label={`Sélectionner ${user.name} ${user.firstname}`}
                            checked={selectedIds.has(user.id)}
                            onChange={() => toggleSelect(user.id)}
                          />
                        </td>
                        <td>
                          <div className="dlm-user-info">
                            <strong className="small">
                              {user.name} {user.firstname}
                            </strong>
                          </div>
                        </td>
                        <td className="text-muted small">{user.email}</td>
                        <td>{getPercentageBadge(user.discount_limit_percentage)}</td>
                        <td>{getStatusBadge(user.discount_limit_active)}</td>
                        <td className="text-muted small">
                          {user.discount_limit_updated_at
                            ? new Date(user.discount_limit_updated_at).toLocaleDateString("fr-FR")
                            : "Jamais"}
                        </td>
                        <td>
                          <div className="dlm-action-button d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditLimit(user)}
                              className="dlm-btn-edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant={user.discount_limit_active ? "outline-danger" : "outline-success"}
                              size="sm"
                              onClick={() => handleToggleLimit(user.id, user.discount_limit_active)}
                              disabled={updatingIds.has(user.id)}
                              className={user.discount_limit_active ? "dlm-btn-toggle-off" : "dlm-btn-toggle-on"}
                            >
                              {updatingIds.has(user.id) ? (
                                <Spinner as="span" animation="border" size="sm" className="dlm-spinner" />
                              ) : user.discount_limit_active ? (
                                <FaToggleOff />
                              ) : (
                                <FaToggleOn />
                              )}
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        {searchTerm ? "Aucun utilisateur trouvé pour cette recherche" : "Aucun utilisateur trouvé"}
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </Table>
          </div>
        </motion.div>
      )}

      {/* Modal individuel */}
      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" className="dlm-modal">
        <Modal.Header closeButton className="dlm-modal-header">
          <Modal.Title className="dlm-modal-title">
            Modifier la limite de remise - {selectedUser?.name} {selectedUser?.firstname}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Pourcentage de limite (%)</Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={newPercentage}
                  onChange={(e) => {
                    const value = Number.parseFloat(e.target.value)
                    if (!isNaN(value) && value >= 0 && value <= 100) setNewPercentage(value)
                    else if (e.target.value === "") setNewPercentage(0)
                  }}
                  className="me-2 dlm-form-control"
                  style={{ width: "100px" }}
                />
                <span className="me-2">%</span>
                <Badge className={newPercentage === 0 ? "dlm-badge-percentage-zero" : "dlm-badge-active"}>
                  {newPercentage}%
                </Badge>
              </div>
              <Form.Range
                min={0}
                max={100}
                step={0.1}
                value={newPercentage}
                onChange={(e) => setNewPercentage(Number.parseFloat(e.target.value))}
                className="dlm-form-range"
              />
              <div className="d-flex justify-content-between mt-1 text-muted small">
                <span>0%</span>
                <span>100%</span>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="active-switch"
                label="Limite active"
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
                className="dlm-form-check"
              />
              <Form.Text className="text-muted">La limite peut être à 0% et le statut peut rester actif si souhaité.</Form.Text>
            </Form.Group>
            <Alert variant="info" className="small dlm-alert-info">
              <strong>Information :</strong> La valeur 0% est valide. Vous pouvez décider si un utilisateur reste
              actif ou non même avec une limite à 0%.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer className="dlm-modal-footer">
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting} className="dlm-btn-cancel">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateLimit} disabled={submitting} className="dlm-btn-primary">
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2 dlm-spinner" />
                Mise à jour...
              </>
            ) : (
              "Mettre à jour"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal global */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} backdrop="static" className="dlm-modal">
        <Modal.Header closeButton className="dlm-modal-header">
          <Modal.Title className="dlm-modal-title">Modifier toutes les limites</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Pourcentage à appliquer à la cible (%)</Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={bulkPercentage}
                  onChange={(e) => {
                    const value = Number.parseFloat(e.target.value)
                    if (!isNaN(value) && value >= 0 && value <= 100) setBulkPercentage(value)
                    else if (e.target.value === "") setBulkPercentage(0)
                  }}
                  className="me-2 dlm-form-control"
                  style={{ width: "100px" }}
                />
                <span className="me-2">%</span>
                <Badge className={bulkPercentage === 0 ? "dlm-badge-percentage-zero" : "dlm-badge-active"}>
                  {bulkPercentage}%
                </Badge>
              </div>
              <Form.Range
                min={0}
                max={100}
                step={0.1}
                value={bulkPercentage}
                onChange={(e) => setBulkPercentage(Number.parseFloat(e.target.value))}
                className="dlm-form-range"
              />
              <div className="d-flex justify-content-between mt-1 text-muted small">
                <span>0%</span>
                <span>100%</span>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="bulk-active-switch"
                label="Rendre actives les limites"
                checked={bulkActive}
                onChange={(e) => setBulkActive(e.target.checked)}
                className="dlm-form-check"
              />
              <Form.Text className="text-muted">L'option active appliquera l'état souhaité aux utilisateurs ciblés.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="apply-filtered-only"
                label={`Appliquer seulement aux ${filteredUsers.length} résultats filtrés`}
                checked={applyToFilteredOnly}
                onChange={(e) => setApplyToFilteredOnly(e.target.checked)}
              />
              <Form.Text className="text-muted">Si décoché, l'opération s'appliquera à tous les utilisateurs.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="apply-selected-only"
                label={`Appliquer seulement aux ${selectedIds.size} utilisateurs sélectionnés`}
                disabled={selectedIds.size === 0}
                checked={applyToSelectedOnly}
                onChange={(e) => setApplyToSelectedOnly(e.target.checked)}
              />
              <Form.Text className="text-muted">Si coché, l'opération s'appliquera uniquement à la sélection manuelle.</Form.Text>
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer className="dlm-modal-footer">
          <Button variant="secondary" onClick={() => setShowBulkModal(false)} disabled={bulkSubmitting} className="dlm-btn-cancel">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateAllLimits} disabled={bulkSubmitting} className="dlm-btn-primary">
            {bulkSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2 dlm-spinner" />
                Application...
              </>
            ) : (
              "Appliquer à cible"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default DiscountLimitsManagement
